import { Component, ComponentInterface, Prop, h, State, Listen, Method } from '@stencil/core';
import { LocationsListingItem } from '../locations-listing-item.interface';

declare global {
	interface Window { 
		mapCenter: google.maps.LatLng | undefined;
		mapObject: google.maps.Map;
	}
}

@Component({
  tag: 'kdx-locations-component',
  styleUrl: 'kdx-locations-component.scss',
  shadow: false,
})
export class StLocationsComponent implements ComponentInterface {
	
	/**
	 * Array of JSONs that represent locations
	 */
	@Prop() locations: string;

	/**
	 * Key value for the google maps API
	 */
	@Prop() googleKey: string;

	/**
	 * Allows user to open marker info windows via card title click
	 */
	@Prop() allowOpenPopup: boolean = true;

	/**
	 * Allows user to focus on card links via marker click
	 */
	@Prop() allowLinkFocus: boolean = true;

	/**
	 * Allows map to display on mobile, by default we only display the map on desktop
	 */
	@Prop() allowMobileMap: boolean = false;

	/**
	 * Ensures that advanced filters have the appropriate script when allowMobileMap is false
	 */
	@Prop() allowAdvancedFilters: boolean = true;

	/**
	 * Initial selection of city select
	 */
	@Prop() initialFormCityValue: string;

	/**
	 * Initially checked types
	 */
	@Prop() initialFormTypesArray: string;

	/**
	 * Initial search radius
	 */
	@Prop() initialFormRadiusValue: number;

	/**
	 * Controls layout classes
	 */
	@Prop() layout: string;

	/**
	 * Country restrictions for autocomplete locations, default US and territories only
	 */
	@Prop() countryRestrictions: string = '["us", "pr", "vi", "gu", "mp"]'

	/**
	 * Lists all locations that should appear in the listing based on the filters
	 */
	@State() filteredLocations: Array<LocationsListingItem> = [];

	/**
	 * A message that conveys a reason for why the results may be unexpected
	 */
	@State() diagnosticMessage?: string;

	/**
	 * Name of marker that should be active on hover state
	 */
	@State() activeMarker: string = "";

	/**
	 * Name of card that should be focused when marker is clicked
	 */
	@State() focusedCard: string = "";


	/**
	 * Checks if location access has been granted
	 */
	@State() userLocation?: google.maps.LatLngLiteral;

	/**
	 * Determines if component should display the map.
	 * Relevant when the allowMobileMap flag is false.
	 */
	@Prop() displayMap: boolean = true;



	/**
	 * Determines the order the locations will be displayed in
	 */
	@Prop() defaultSort: string;

	/**
	 * Breakpoint to determine whether mobile or desktop display should occur.
	 */
	screenBreakpoint: number = 768;


	/**
	 * Tracks value from simple city select, relevant for filter changes
	 */
	formCityValue: string = null;

	/**
	 * Tracks which checkboxes are checked that determine type, relevant for filter changes
	 */
	formTypesArray: Array<string> = [];

	/**
	 * Tracks search radius used for finding locations with advanced filters
	 */
	formRadiusValue: number = 0;

	/**
	 * Gets text input for user as epicenter of search radius
	 */
	autocomplete: google.maps.places.Autocomplete;
	autocompleteValue: google.maps.places.PlaceResult = null;

	/**
	 * This is the locations property parsed into an array
	 */
	locationsArray: Array<LocationsListingItem>;

	/**
	 * Listens for any title clicks in locations-listing cards
	 * Sends that information to the locations-map to reevaluate active marker
	 * @param event contains name of item being hovered over
	 */
	@Listen('listingCardTitleClick')
	handleActiveMarker(event: CustomEvent<string>) {
		if (this.allowMobileMap || (!this.allowMobileMap && window.innerWidth >= this.screenBreakpoint)) {
			this.activeMarker = event.detail;
		}
	}

	/**
	 * Listens for Popups closing, resets the active marker to empty.
	 * @param event custom event triggered by Popup close click
	 */
	@Listen('closeclick')
	handlePopupClose() {
		this.activeMarker = '';
		this.focusedCard = '';
	}


	@Listen('keydown.enter')
	handleEnter(){
		// console.log("ENTER");
	}

	
	/**
	 * Listens for any changes to markers being clicked in locations-map
	 * Sends that information to the locations-listing component to reevaluate focus
	 * @param event contains id of marker being clicked on
	 */
	@Listen('markerClicked')
	handleClickedMarker(event: CustomEvent<string>) {
		this.focusedCard = event.detail;
		this.activeMarker = event.detail;
	}

	/**
	 * Used when not rendering the map in mobile.
	 */
	@Listen('resize', {target: 'window'})
	handleWindowResize() {
		if (!this.allowMobileMap && !this.displayMap && window.innerWidth >= this.screenBreakpoint) {
			this.displayMap = true;
		} else if (!this.allowMobileMap && this.displayMap && window.innerWidth < this.screenBreakpoint) {
			this.displayMap = false;
		}
	}

	@Listen('submit')
	handleFormSubmission(event: SubmitEvent) {
		event.preventDefault();
	}

	/**
	 * Determines if any change event caused by children should trigger filter updates
	 * @param event listens for changes made to input elements
	 */
	@Listen('change')
	handleFormChanges(event: Event) {

		const target = event.target as HTMLInputElement;

		if (target.classList.contains('locations-city-select')) {
			// Handles simple select for city locations

			this.formCityValue = target.value === '' ? null : target.value;

			this.updateFilteredLocations();

		} else if (target.classList.contains('locations-type-check')) {
			// Handles type filters managed by checkboxes

			if (target.checked) {
				this.formTypesArray.push(target.value);
			} else {
				this.formTypesArray.splice(this.formTypesArray.indexOf(target.value), 1);
			}

			const typesMenuText: HTMLElement = document.querySelector('.locations-filter .kdx-dropdown-list > a .dropdown-text');
			if (typesMenuText) {
				typesMenuText.innerText = this.formTypesArray.length ? this.formTypesArray.join(', ') : 'Type';
			}

			this.updateFilteredLocations();

		} else if (target.classList.contains('locations-radius-select')) {
			// Handles select that defines search radius

			this.formRadiusValue = parseInt(target.value);
			this.updateFilteredLocations();
		} 
	}

	/**
	 * Reset places autocomplete
	 */
	private resetAutocomplete() {
		this.autocompleteValue = null;
		this.activeMarker = '';
		this.updateFilteredLocations();
		const input: HTMLInputElement = document.querySelector('.locations-text-search');
		input.value = null;

		this.toggleClearBtn(false);
	}

	private toggleClearBtn(show) {
		const clearBtn: HTMLElement = document.querySelector('.clear-places-autocomplete');
		if (clearBtn) {
			clearBtn.style.display = show ? 'block' : 'none';
		}
	}

	/**
	 * Updates the filter results based on the most recent form values.
	 */
	private updateFilteredLocations() {
		let filteredLocations: Array<LocationsListingItem> = [];

		let currentPosition: google.maps.LatLng | undefined;
		if (this.autocompleteValue || this.userLocation)
			currentPosition =
				this.autocompleteValue?.geometry?.location ??
				new google.maps.LatLng(this.userLocation);

		let skipAdvancedFilters = !this.formRadiusValue || !currentPosition;

		window.mapCenter = currentPosition;

		for (const location of this.locationsArray) {

			//let passesCityFilter = this.formCityValue === null || location.address.indexOf(this.formCityValue) > -1;

			// let typeIsInFormTypeArray = false;
			// location.type.forEach((type) => {
			// 	if (this.formTypesArray.indexOf(type) > -1) {
			// 		typeIsInFormTypeArray = true;
			// 	}
			// });

			//let passesTypeFilters = location.type === undefined || this.formTypesArray.length === 0 || typeIsInFormTypeArray;

			let passesAdvancedFilters = skipAdvancedFilters || this.locationWithinRange(this.formRadiusValue, currentPosition, new google.maps.LatLng(location.latitude, location.longitude));

			// if (passesCityFilter && passesTypeFilters && passesAdvancedFilters) {
			if (passesAdvancedFilters) {
				filteredLocations.push(location);
			}
		}

		if (filteredLocations.length) {
			this.filteredLocations = filteredLocations;
			this.diagnosticMessage = undefined;
		}
		else {
			this.filteredLocations = [...this.locationsArray];
			const miles = this.formRadiusValue.toLocaleString('en-US', {
				style: 'unit',
				unit: 'mile',
				unitDisplay: 'long',
				maximumFractionDigits: 1
			});
			this.diagnosticMessage = `No results found within ${miles} of your location.`;
		}
	}

	/**
	 * Determines if point A and B are at a distance that does not exceed the max
	 * @param max determines the maximum distance acceptable between two points (in miles)
	 * @param pointA first point on the map (LatLng Object)
	 * @param pointB second point on the map (LatLng Object)
	 */
	private locationWithinRange(max: number, pointA: google.maps.LatLng, pointB: google.maps.LatLng) {

		let locationDist = google.maps.geometry.spherical.computeDistanceBetween(pointA, pointB) / 1609;


		return locationDist <= max;
	}

	/**
	 * parses locations string into json
	 * @param locations string to parse into JSON
	 */
	private getLocations(locations) {
		return JSON.parse(locations);
	}

	/**
	 * Builds the autocomplete control used for the advanced filter features
	 */
	@Method()
	async buildAutocomplete() {

		let input = document.querySelector('.locations-text-search') as HTMLInputElement;

		// listens for enter to be pressed
		input.addEventListener('keydown', e => {
			if(e.key !== 'Enter')
			return;

			if(input.querySelector('locationsSearchBox')){
				return
			}

			//stops enter from executing
			e.stopImmediatePropagation()

			// If no place auto complete item is already down-arrow'ed into, choose the first item
			if (!document.querySelector('.pac-item-selected')) {
				//Down Arrow
				input.dispatchEvent(
					new KeyboardEvent('keydown', {
						//@ts-ignore
						keyCode: 40
					})
				);
			}

			//Enter
			input.dispatchEvent(
				new KeyboardEvent('keydown', {
					//@ts-ignore
					keyCode: 13
				})
			)
		})

		if (input !== null) {

			const bounds = new google.maps.LatLngBounds();
			this.locationsArray.forEach(location => {
				bounds.extend({
					lat: location.latitude,
					lng: location.longitude
				});
			});

			this.autocomplete = new google.maps.places.Autocomplete(input, {
				bounds,
				strictBounds: true,
				componentRestrictions: { country: JSON.parse(this.countryRestrictions) }
			});

			this.autocomplete.addListener("place_changed", () => {
				const place = this.autocomplete.getPlace();

				if (place) {
					this.activeMarker = '';
					this.autocompleteValue = place;
					this.updateFilteredLocations();
					this.toggleClearBtn(true);
				}
			});
		}
	}

	/**
	 * Determines if map should be displayed initially
	 */
	async componentWillLoad() {

		if (!this.allowMobileMap && window.innerWidth < this.screenBreakpoint) {
			this.displayMap = false;
		}

		if (this.initialFormCityValue) {
			this.formCityValue = this.initialFormCityValue;
		}

		if (this.initialFormTypesArray) {
			this.formTypesArray = JSON.parse(this.initialFormTypesArray);
		}

		if (this.initialFormRadiusValue) {
			this.formRadiusValue = this.initialFormRadiusValue;
		}

		this.locationsArray =  await this.getLocations(this.locations);


		if(this.defaultSort){
			this.locationsArray.sort((a,b) => (a.name > b.name) ? 1 : -1)
		}


		let mapInitialized = false;
		navigator.geolocation.getCurrentPosition(
			position => {
				this.userLocation = {
					lat: position.coords.latitude,
					lng: position.coords.longitude
				};
				if (mapInitialized)
					this.updateFilteredLocations();
			}
		)

		// Waits for Google API to load before using it
		// @ts-ignore
		window.initMap = async () => {
			mapInitialized = true;
			this.updateFilteredLocations();

			if (this.displayMap) {
				await customElements.whenDefined('kdx-locations-map');
				const mapElement = document.querySelector('kdx-locations-map');
				await mapElement.buildMap();
			}

			if (window.mapObject) {
				google.maps.event.addListenerOnce(window.mapObject, 'idle', () => {
					this.buildAutocomplete();
				});
			}
			else {
				this.buildAutocomplete();
			}
		};

		const clearBtn: HTMLElement = document.querySelector('.clear-places-autocomplete');
		if (clearBtn) {
			clearBtn.onclick = (e) => {
				e.preventDefault();
				this.resetAutocomplete();
			}
		}

    const locationsContent = document.querySelector('.locations-content');
    const panelWrapper = document.querySelector('.location-panel-wrapper');
    if (locationsContent && panelWrapper) {
      const contentPlacer = document.createElement('div');
      contentPlacer.classList.add('non-editor');
      contentPlacer.innerHTML = locationsContent.innerHTML;
      panelWrapper.insertBefore(contentPlacer, panelWrapper.childNodes[0]);
    }
	}
 
	render() {
		if (this.displayMap) {
			return (
				<div class={"locations-component " + this.layout}>
					<div class="container">
						<div class="locations-row">
							<div class="locations-panel-col">
								<div class="locations-panel">
									<slot />
								</div>
							</div>
							<div class="locations-listing-wrapper">
								<kdx-locations-listing locations={this.filteredLocations} diagnosticMessage={this.diagnosticMessage} focusedCard={this.focusedCard} allowOpenPopup={this.allowOpenPopup}></kdx-locations-listing>
              </div>
							<div class="locations-map-col">
								<kdx-locations-map
									locationsArray={this.filteredLocations}
									locations={this.locations}
									googleKey={this.googleKey}
									activeMarker={this.activeMarker}
									allowLinkFocus={this.allowLinkFocus}
									isChildComponent={true}>
								</kdx-locations-map>
							</div>
						</div>
					</div>
				</div>	
			);
		} else if (this.allowAdvancedFilters) {
			return (
				<div class={"locations-component " + this.layout}>
					<div class="container">
						<div class="row">
							<div class="col-12 locations-panel-col">
								<div class="locations-panel">
									<slot />
									<kdx-locations-listing locations={this.filteredLocations} diagnosticMessage={this.diagnosticMessage} focusedCard={this.focusedCard} allowOpenPopup={this.allowOpenPopup}></kdx-locations-listing>
									<script defer src={"https://maps.googleapis.com/maps/api/js?key=" + this.googleKey + "&callback=initMap&libraries=geometry,places"}></script>
								</div>
							</div>
						</div>
					</div>
				</div>	
			);
		} else {
			return (
				<div class={"locations-component " + this.layout}>
					<div class="container">
						<div class="row">
							<div class="col-12 locations-panel-col">
								<div class="locations-panel">
									<slot />
									<kdx-locations-listing locations={this.filteredLocations} diagnosticMessage={this.diagnosticMessage} focusedCard={this.focusedCard} allowOpenPopup={this.allowOpenPopup}></kdx-locations-listing>
								</div>
              </div>
						</div>
					</div>
				</div>	
			);
		}
	}
}
